// The shared write-UI kit for every operator screen.
//
// It began (2026-08-09) as the private toolkit of the three catalogue editors.
// It moved here on 2026-08-10 when the user asked for EVERY menu item to be
// editable rather than view-only, which replaced the read-only data-screens rule
// outright. One kit, so thirty screens cannot drift into thirty dialects of the
// same form.
//
// ── Retire vs delete ──────────────────────────────────────────────────────────
// These are DIFFERENT ACTIONS and the kit keeps them apart on purpose.
//
//   ArchiveDialog / ArchiveRow — reversible. For a row that HISTORY STILL POINTS
//   AT: bookings name a service, customer_packages name a package, invoices name
//   a customer. Deleting one of those does not tidy the past, it breaks it. So
//   the row stays and stops being offered.
//
//   DeleteDialog / DeleteRow — permanent. For a row nothing references: a time
//   block, an unused template, a notification schedule. Here archiving would be
//   hoarding — a list of retired typos nobody can ever clear.
//
// The product owner's ruling (2026-08-10) is hard-delete when unreferenced,
// archive when referenced. A caller picks the dialog that matches; where only the
// server knows which applies, it refuses the delete and the caller archives
// instead. Neither is ever a bare window.confirm saying "Are you sure?" — the
// operator has to be told which of the two is about to happen.

import { useState, type ComponentProps, type ReactNode } from 'react'
import { Archive, Loader2, RotateCcw, Save, Trash2 } from 'lucide-react'
import { useBranding } from '@/contexts/BrandingContext'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

// ─── Form fields ───────────────────────────────────────────────────────────────

export function Labelled({
  label,
  hint,
  required,
  children,
}: {
  label: string
  hint?: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <div className="mb-2.5">
      <label className="mb-1 block text-[11px] font-bold text-muted-foreground">
        {label}
        {required ? <span className="ml-0.5 text-rose-600">*</span> : null}
      </label>
      {children}
      {hint ? <p className="mt-1 text-[11px] text-muted-foreground/70">{hint}</p> : null}
    </div>
  )
}

const FIELD_CLASS =
  'h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] text-foreground ' +
  'placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-[3px] ' +
  'focus-visible:ring-ring/50 disabled:opacity-50'

export function TextField({
  value,
  onChange,
  placeholder,
  disabled,
  type = 'text',
  autoComplete,
  ariaLabel,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  disabled?: boolean
  /** 'password' for a credential, so it is neither shown nor echoed on screen. */
  type?: 'text' | 'password'
  autoComplete?: string
  ariaLabel?: string
}) {
  return (
    <input
      type={type}
      className={FIELD_CLASS}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      autoComplete={autoComplete}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

/**
 * Empty is a real value and maps to `null`, not 0 — "no expiry" and "expires in 0
 * days" are different promises, and so are "no cap" and "needs 0 resources".
 * The raw string is held in state so a half-typed "1." or a cleared box does not
 * snap back to a number under the operator's cursor.
 */
export function NumberField({
  value,
  onChange,
  placeholder,
  disabled,
  min,
  step,
  prefix,
  suffix,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  disabled?: boolean
  min?: number
  step?: number
  prefix?: string
  suffix?: string
}) {
  return (
    <div className="flex items-center gap-1.5">
      {prefix ? <span className="text-[13px] text-muted-foreground">{prefix}</span> : null}
      <input
        type="number"
        inputMode="decimal"
        className={FIELD_CLASS}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        step={step}
        onChange={(e) => onChange(e.target.value)}
      />
      {suffix ? <span className="whitespace-nowrap text-[13px] text-muted-foreground">{suffix}</span> : null}
    </div>
  )
}

export function SelectField<T extends string>({
  value,
  onChange,
  options,
  disabled,
}: {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: string }[]
  disabled?: boolean
}) {
  return (
    <select
      className={FIELD_CLASS}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as T)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  )
}

export function TextAreaField({
  value,
  onChange,
  placeholder,
  disabled,
  rows = 4,
  mono,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  disabled?: boolean
  rows?: number
  mono?: boolean
}) {
  return (
    <textarea
      className={
        'w-full rounded-md border border-input bg-transparent px-2.5 py-2 text-[13px] text-foreground ' +
        'placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-[3px] ' +
        'focus-visible:ring-ring/50 disabled:opacity-50 ' +
        (mono ? 'font-mono text-[12px] leading-[17px]' : '')
      }
      rows={rows}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

/**
 * Wall-clock time, `HH:mm`.
 *
 * The API's time columns are bare local times with no date and no zone, which is
 * exactly what <input type="time"> produces — so nothing here ever constructs a
 * Date. Building one would attach today's date and the VIEWER's offset to a value
 * that means "9am wherever this business is", which is the shift `lib/format.ts`
 * exists to avoid everywhere else.
 *
 * Postgres hands back `09:00:00`; the input only accepts `09:00`, and silently
 * renders blank otherwise. Trimming the seconds is therefore load-bearing.
 */
export function TimeField({
  value,
  onChange,
  disabled,
  step,
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  /** Seconds between selectable values. Omit for the browser default of 60. */
  step?: number
}) {
  return (
    <input
      type="time"
      className={FIELD_CLASS}
      value={trimSeconds(value)}
      disabled={disabled}
      step={step}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

/** `09:00:00` → `09:00`. Empty and already-short values pass through unchanged. */
export function trimSeconds(value: string): string {
  const m = /^(\d{2}:\d{2})(:\d{2})?/.exec(value ?? '')
  return m ? m[1] : ''
}

export function DateField({
  value,
  onChange,
  disabled,
  min,
  max,
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  min?: string
  max?: string
}) {
  return (
    <input
      type="date"
      className={FIELD_CLASS}
      value={(value ?? '').slice(0, 10)}
      disabled={disabled}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

/**
 * `YYYY-MM-DDTHH:mm` — the platform's API datetime format exactly (tenant-local
 * naive, both directions), so the value round-trips with no conversion at all.
 * Seconds are sliced off because the input rejects them.
 */
export function DateTimeField({
  value,
  onChange,
  disabled,
  min,
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  min?: string
}) {
  return (
    <input
      type="datetime-local"
      className={FIELD_CLASS}
      value={(value ?? '').slice(0, 16)}
      disabled={disabled}
      min={min}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

/**
 * An on/off business rule. Rendered as a labelled row rather than a bare
 * checkbox: several of these decide things like whether GST is claimed or whether
 * consent is required, and a naked tick box beside a short label is too easy to
 * misread in either direction.
 */
export function ToggleField({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  hint?: string
  disabled?: boolean
}) {
  return (
    <label className="mb-2.5 flex cursor-pointer items-start gap-2.5">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 accent-foreground disabled:opacity-50"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-foreground">{label}</span>
        {hint ? <span className="mt-0.5 block text-[11px] text-muted-foreground/70">{hint}</span> : null}
      </span>
    </label>
  )
}

/**
 * Weekday picker, 1=Monday … 7=Sunday — the platform's `day_of_week` encoding
 * (`getDayOfWeek` in _shared/availability.ts), NOT JavaScript's 0=Sunday. Getting
 * that wrong shifts a whole week's opening hours by one day, so the mapping lives
 * here once instead of in each screen.
 */
export const WEEKDAYS: { value: number; short: string; long: string }[] = [
  { value: 1, short: 'Mon', long: 'Monday' },
  { value: 2, short: 'Tue', long: 'Tuesday' },
  { value: 3, short: 'Wed', long: 'Wednesday' },
  { value: 4, short: 'Thu', long: 'Thursday' },
  { value: 5, short: 'Fri', long: 'Friday' },
  { value: 6, short: 'Sat', long: 'Saturday' },
  { value: 7, short: 'Sun', long: 'Sunday' },
]

export function DayPicker({
  selected,
  onToggle,
  disabled,
}: {
  selected: number[]
  onToggle: (day: number) => void
  disabled?: boolean
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {WEEKDAYS.map((d) => {
        const on = selected.includes(d.value)
        return (
          <button
            key={d.value}
            type="button"
            disabled={disabled}
            onClick={() => onToggle(d.value)}
            aria-pressed={on}
            aria-label={d.long}
            className={
              'w-11 rounded-md border px-2 py-1 text-[12px] font-semibold disabled:opacity-40 ' +
              (on
                ? 'border-foreground/70 bg-foreground text-background'
                : 'border-border bg-card text-foreground hover:bg-muted')
            }
          >
            {d.short}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Multi-select as toggleable chips. Used for a service's resource pool and a
 * package's covered services — both are "pick several from a short list", and a
 * native multi-select is unusable on a phone.
 */
export function ChipMultiSelect({
  options,
  selected,
  onToggle,
  disabled,
  emptyText,
}: {
  options: { id: string; label: string; sublabel?: string }[]
  selected: string[]
  onToggle: (id: string) => void
  disabled?: boolean
  emptyText?: string
}) {
  if (options.length === 0) {
    return <p className="text-[12.5px] text-muted-foreground">{emptyText ?? 'Nothing to choose from.'}</p>
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = selected.includes(o.id)
        return (
          <button
            key={o.id}
            type="button"
            disabled={disabled}
            onClick={() => onToggle(o.id)}
            aria-pressed={on}
            className={
              'rounded-md border px-2 py-1 text-left text-[12px] disabled:opacity-40 ' +
              (on
                ? 'border-foreground/70 bg-foreground text-background'
                : 'border-border bg-card text-foreground hover:bg-muted')
            }
          >
            {o.label}
            {o.sublabel ? (
              <span className={on ? 'ml-1 opacity-70' : 'ml-1 text-muted-foreground'}>{o.sublabel}</span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

// ─── Header buttons ────────────────────────────────────────────────────────────

/** A screen's one primary action, filled in the tenant's brand colour. */
export function PrimaryHeaderButton({ className, style, ...props }: ComponentProps<'button'>) {
  const branding = useBranding()
  return (
    <button
      type="button"
      {...props}
      className={cn(
        'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-3 text-[13px] font-semibold whitespace-nowrap text-white shadow-xs',
        'transition-[filter] hover:brightness-110 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none disabled:opacity-40 disabled:hover:brightness-100',
        className,
      )}
      style={{ backgroundColor: branding.primaryColor, ...style }}
    />
  )
}

/** A secondary icon action in a header (settings, and the like). */
export const HEADER_ICON_BUTTON =
  'shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40'

export function SaveButton({
  onClick,
  saving,
  disabled,
  label = 'Save',
}: {
  onClick: () => void
  saving: boolean
  disabled?: boolean
  label?: string
}) {
  return (
    <PrimaryHeaderButton
      onClick={onClick}
      disabled={disabled || saving}
      aria-label={label}
    >
      {saving ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <Save size={15} aria-hidden />}
      {saving ? 'Saving…' : label}
    </PrimaryHeaderButton>
  )
}

// ─── Archive / restore ─────────────────────────────────────────────────────────

export interface ArchiveCopy {
  /** e.g. "service". Used in the button and the dialog title. */
  noun: string
  /** What retiring actually stops. One short sentence. */
  effect: string
  /** What survives it. One short sentence — this is the reassurance that makes
   *  archiving obviously different from deleting. */
  preserved: string
}

// Per-dataset wording lives with its own screens — see
// components/catalogue/archiveCopy.ts. It is defined once per dataset because the
// list row and the editor both explain the same action, and an operator told two
// different stories about what retiring does will believe the more alarming one.

export function ArchiveRow({
  isActive,
  copy,
  name,
  busy,
  onConfirm,
}: {
  isActive: boolean
  copy: ArchiveCopy
  name: string
  busy: boolean
  onConfirm: (nextActive: boolean) => void
}) {
  const [open, setOpen] = useState(false)

  // Restoring is not destructive and needs no confirmation — it puts something
  // back on the shelf. Only retiring asks.
  if (!isActive) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => onConfirm(true)}
        className="flex items-center gap-1.5 text-[12.5px] font-semibold text-foreground hover:underline disabled:opacity-40"
      >
        <RotateCcw size={14} aria-hidden />
        Restore this {copy.noun}
      </button>
    )
  }

  return (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-[12.5px] font-semibold text-rose-700 hover:underline disabled:opacity-40"
      >
        <Archive size={14} aria-hidden />
        Retire this {copy.noun}
      </button>

      <ArchiveDialog
        open={open}
        onOpenChange={setOpen}
        copy={copy}
        name={name}
        busy={busy}
        onConfirm={() => {
          setOpen(false)
          onConfirm(false)
        }}
      />
    </>
  )
}

// ─── Delete ────────────────────────────────────────────────────────────────────

export interface DeleteCopy {
  /** e.g. "time block". Used in the button and the dialog title. */
  noun: string
  /** What disappears. One short sentence. */
  effect: string
  /** Only set when the server may refuse — says what would make it refuse, so a
   *  refusal reads as a rule rather than a bug. */
  refusedWhen?: string
}

/**
 * Permanent removal, for rows nothing references. Deliberately NOT the same
 * component as ArchiveDialog even though they look similar: an operator has to be
 * able to tell at a glance which of the two is about to happen, and the honest
 * signal is the one written in the dialog, not the icon on the button.
 *
 * There is no "you can bring it back" line here, because you cannot.
 */
export function DeleteDialog({
  open,
  onOpenChange,
  copy,
  name,
  busy,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  copy: DeleteCopy
  name: string
  busy: boolean
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Delete {name || `this ${copy.noun}`}?</DialogTitle>
          <DialogDescription>{copy.effect}</DialogDescription>
        </DialogHeader>

        <p className="text-[12.5px] font-semibold text-rose-700">
          This cannot be undone.
        </p>
        {copy.refusedWhen ? (
          <p className="text-[12.5px] text-muted-foreground">{copy.refusedWhen}</p>
        ) : null}

        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="rounded-md border border-border bg-card px-3 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-md bg-rose-700 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-rose-800 disabled:opacity-40"
          >
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <Trash2 size={14} aria-hidden />}
            Delete {copy.noun}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** The delete counterpart of ArchiveRow, for the foot of an editor screen. */
export function DeleteRow({
  copy,
  name,
  busy,
  onConfirm,
}: {
  copy: DeleteCopy
  name: string
  busy: boolean
  onConfirm: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-[12.5px] font-semibold text-rose-700 hover:underline disabled:opacity-40"
      >
        <Trash2 size={14} aria-hidden />
        Delete this {copy.noun}
      </button>

      <DeleteDialog
        open={open}
        onOpenChange={setOpen}
        copy={copy}
        name={name}
        busy={busy}
        onConfirm={() => {
          setOpen(false)
          onConfirm()
        }}
      />
    </>
  )
}

export function ArchiveDialog({
  open,
  onOpenChange,
  copy,
  name,
  busy,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  copy: ArchiveCopy
  name: string
  busy: boolean
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Retire {name || `this ${copy.noun}`}?</DialogTitle>
          <DialogDescription>{copy.effect}</DialogDescription>
        </DialogHeader>

        <p className="text-[12.5px] text-muted-foreground">{copy.preserved}</p>
        <p className="text-[12.5px] text-muted-foreground">
          You can bring it back at any time — switch the list to “Including retired”, open it and press Restore.
        </p>

        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="rounded-md border border-border bg-card px-3 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-md bg-rose-700 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-rose-800 disabled:opacity-40"
          >
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <Archive size={14} aria-hidden />}
            Retire {copy.noun}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
